/**
 * Enhanced API client for TimechainStats
 *
 * Features:
 * - Promise.allSettled prevents cascade failures (one bad endpoint doesn't break all)
 * - Schema validation catches data format issues
 * - Metadata tracking (success/error status, timestamps per endpoint)
 * - Timeout handling (10 second limit per request)
 */

import axios from 'axios';
import { validateEndpoint } from './dataValidation.utils';

/**
 * Extract endpoint name from URL
 * @param {string} url - API URL
 * @returns {string} Endpoint name (e.g., 's091', 'ts04')
 */
const extractEndpointName = (url) => {
  const match = url.match(/\/api\/([^/]+)\//);
  return match ? match[1] : 'unknown';
};

/**
 * Fetch data from multiple URLs with validation and error resilience
 *
 * Uses Promise.allSettled instead of Promise.all to prevent cascade failures.
 * If 1 endpoint fails, the other 21 still succeed.
 *
 * @param {string[]} urls - Array of API URLs to fetch
 * @param {Function} [onProgress] - Optional callback fired per endpoint as soon as it settles
 *   onProgress(endpointName, endpointData, metadata) — endpointData is unwrapped (response.data[endpointName])
 * @returns {Promise<Object>} { data: Object, metadata: Object }
 */
export const fetchWithValidation = async (urls, onProgress) => {
  console.group('[API Client] Fetching data from', urls.length, 'endpoints');
  const startTime = Date.now();

  // Fetch all URLs in parallel with individual error handling
  const results = await Promise.allSettled(
    urls.map(async (url) => {
      const endpointName = extractEndpointName(url);
      const requestStart = Date.now();

      try {
        // Fetch with 10 second timeout
        const response = await axios.get(url, {
          timeout: 10000,
          headers: {
            'Accept': 'application/json'
          }
        });

        const requestTime = Date.now() - requestStart;

        // Validate response data
        const validation = validateEndpoint(endpointName, response.data);

        // Log validation warnings (not errors)
        if (validation.warnings && validation.warnings.length > 0) {
          console.warn(`[${endpointName}] Validation warnings:`, validation.warnings);
        }

        // Log validation errors
        if (!validation.valid) {
          console.error(`[${endpointName}] Validation failed:`, validation.errors);
        }

        const status = validation.valid ? 'success' : 'invalid';
        const timestamp = Date.now();

        // Incremental hydration: notify caller as soon as this endpoint settles
        if (onProgress) {
          try {
            onProgress(endpointName, response.data?.[endpointName], {
              status,
              timestamp,
              requestTime,
              warnings: validation.warnings,
              errors: validation.errors
            });
          } catch (cbErr) {
            console.warn(`[${endpointName}] onProgress callback error:`, cbErr.message);
          }
        }

        return {
          endpoint: endpointName,
          status,
          data: response.data,
          validation,
          timestamp,
          requestTime
        };
      } catch (error) {
        const requestTime = Date.now() - requestStart;

        // Determine error type
        let errorType = 'unknown';
        if (error.code === 'ECONNABORTED') {
          errorType = 'timeout';
        } else if (error.response) {
          errorType = `http_${error.response.status}`;
        } else if (error.request) {
          errorType = 'network';
        }

        console.error(`[${endpointName}] Fetch error (${errorType}):`, error.message);

        return {
          endpoint: endpointName,
          status: 'error',
          error: error.message,
          errorType,
          timestamp: Date.now(),
          requestTime
        };
      }
    })
  );

  // Process results and separate data from metadata
  const data = {};
  const metadata = {};

  results.forEach((result) => {
    if (result.status === 'fulfilled') {
      const value = result.value;

      if (value.status === 'success') {
        // Successful fetch with valid data
        data[value.endpoint] = value.data[value.endpoint];
        metadata[value.endpoint] = {
          status: 'success',
          timestamp: value.timestamp,
          requestTime: value.requestTime,
          warnings: value.validation.warnings
        };
      } else if (value.status === 'invalid') {
        // Fetch succeeded but validation failed
        // Still include data (let components decide how to handle)
        data[value.endpoint] = value.data[value.endpoint];
        metadata[value.endpoint] = {
          status: 'invalid',
          timestamp: value.timestamp,
          requestTime: value.requestTime,
          errors: value.validation.errors,
          warnings: value.validation.warnings
        };
      } else {
        // Fetch error
        metadata[value.endpoint] = {
          status: 'error',
          error: value.error,
          errorType: value.errorType,
          timestamp: value.timestamp,
          requestTime: value.requestTime
        };
      }
    } else {
      // Promise rejected (shouldn't happen with allSettled, but handle it)
      console.error('[API Client] Promise rejected:', result.reason);
    }
  });

  const totalTime = Date.now() - startTime;

  // Summary statistics
  const summary = {
    total: urls.length,
    success: Object.values(metadata).filter(m => m.status === 'success').length,
    invalid: Object.values(metadata).filter(m => m.status === 'invalid').length,
    error: Object.values(metadata).filter(m => m.status === 'error').length,
    totalTime,
    avgRequestTime: Math.round(
      Object.values(metadata).reduce((sum, m) => sum + (m.requestTime || 0), 0) / urls.length
    )
  };

  console.log('[API Client] Fetch complete:', summary);
  console.groupEnd();

  return {
    data,
    metadata,
    summary
  };
};

/**
 * Enrich API data with fallback calculations
 * This merges API data with calculated metrics from other sources
 *
 * @param {Object} apiData - Data from fetchWithValidation
 * @param {Object} sources - Additional data sources (recentBlocks, diamondback, etc.)
 * @returns {Object} Enriched data with calculated fallbacks
 */
export const enrichWithFallbacks = (apiData, sources) => {
  // For now, just return the API data
  // In the future, this will merge with calculated metrics from:
  // - Diamondback stats
  // - Mempool WebSocket calculations
  // - Cached values
  return apiData;
};
